import { Globe, Users } from "lucide-react";
import { Label } from "../../components/ui/label";
import { cn } from "../../lib/utils";

export type Audience = "PUBLIC" | "VERIFIED";

const OPTIONS: { value: Audience; label: string; hint: string; icon: typeof Globe }[] = [
  {
    value: "PUBLIC",
    label: "Everyone",
    hint: "Visitors and members can see this",
    icon: Globe,
  },
  {
    value: "VERIFIED",
    label: "Members only",
    hint: "Only approved members can see this",
    icon: Users,
  },
];

/** Maps any stored visibility to the two audiences; legacy SUBSCRIBER counts as members only. */
export function toAudience(visibility: string | null | undefined): Audience {
  return visibility === "PUBLIC" ? "PUBLIC" : "VERIFIED";
}

interface AudienceSelectProps {
  value: Audience;
  onChange: (value: Audience) => void;
  className?: string;
}

export function AudienceSelect({ value, onChange, className }: AudienceSelectProps) {
  return (
    <div className={cn("space-y-2", className)}>
      <Label>Who can see this?</Label>
      <div role="radiogroup" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {OPTIONS.map(({ value: option, label, hint, icon: Icon }) => {
          const selected = value === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(option)}
              className={cn(
                "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                selected
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border hover:bg-muted/50",
              )}
            >
              <Icon className={cn("mt-0.5 h-4 w-4", selected ? "text-primary" : "text-muted-foreground")} />
              <span>
                <span className="block text-sm font-medium">{label}</span>
                <span className="block text-xs text-muted-foreground">{hint}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
